import {
  Badge,
  Button,
  ErrorText,
  FormField,
  MessageBox,
  ModalDialog,
  Select,
  Table,
  TableCell,
  TableEmptyState,
  TableErrorState,
  TableHeadCell,
  TableLoadingState,
  TableRow,
} from "@rothoblaas/design-system";
import { FC, useContext, useMemo, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useRowSelection from "../../hooks/useRowSelection";
import useI18n from "../../hooks/useTranslations";
import {
  ACCESS_SCOPE_TITLES,
  ACCESS_SCOPES,
  AccessRuleImport,
  AccessRuleStatus,
  AccessTarget,
} from "../../http/types/accessControl";
import { getAccessTargetKey, useAccessControlApi } from "../../http/useAccessControl";
import { useModulesApi } from "../../http/useModules";

type ImportAccessRulesModalProps = {
  // the app or module to import into
  target: AccessTarget;
  onClose: () => void;
};

// An app or a module to import from.
type SourceOption = { target: AccessTarget; key: string; label: string };

type SourceGroup = { label: string; options: SourceOption[] };

// The current user's own rule can't be imported, and a rule with the same status is already present.
const isImportable = (rule: AccessRuleImport) => !rule.isCurrentUser && rule.currentStatus !== rule.status;

const ImportAccessRulesModal: FC<ImportAccessRulesModalProps> = ({ target, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const accessControlApi = useAccessControlApi();
  const aborter = useRef(new AbortController());
  // every module (no pagination parameters)
  const { data: modules } = useModulesApi().useAdminModules({}, aborter.current.signal);

  // The apps, then the modules by name, but the one to import into.
  const targetKey = getAccessTargetKey(target);
  const sourceGroups = useMemo<SourceGroup[]>(() => {
    const appOptions = ACCESS_SCOPES.map((scope) => ({
      target: { scope },
      key: getAccessTargetKey({ scope }),
      label: ACCESS_SCOPE_TITLES[scope],
    }));
    const moduleOptions = [...(modules?.items ?? [])]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((module) => ({
        target: { moduleId: module.id },
        key: getAccessTargetKey({ moduleId: module.id }),
        label: module.name,
      }));

    return [
      { label: i18n.translation.accessControl.applications, options: appOptions.filter((x) => x.key !== targetKey) },
      { label: i18n.translation.accessControl.modules, options: moduleOptions.filter((x) => x.key !== targetKey) },
    ];
  }, [modules, targetKey, i18n]);

  const [from, setFrom] = useState<SourceOption>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: rules, error } = accessControlApi.getRulesToImport(target, from?.target, aborter.current.signal);

  const importableEmails = useMemo(() => rules?.filter(isImportable).map((rule) => rule.email) ?? [], [rules]);
  // New rules start selected, the ones overwriting a different status only on request.
  const newEmails = useMemo(
    () => rules?.filter((rule) => isImportable(rule) && !rule.currentStatus).map((rule) => rule.email) ?? [],
    [rules],
  );
  const selection = useRowSelection(importableEmails, newEmails);

  const importRules = () => {
    if (!from) {
      return;
    }

    setIsSubmitting(true);
    accessControlApi
      .importRules(target, from.target, selection.selectedIds)
      .then(() => {
        dispatch(showSuccessNotification(i18n.translation));
        onClose();
      })
      .catch((err) => dispatch(showErrorNotification(i18n, err)))
      .finally(() => setIsSubmitting(false));
  };

  const renderStatus = (status: AccessRuleStatus) => (
    <Badge color={status === "allowed" ? "success" : "error"}>
      {status === "allowed"
        ? i18n.translation.accessControl.rules.statusAllowed
        : i18n.translation.accessControl.rules.statusBlocked}
    </Badge>
  );

  const tableStatus = error ? (
    <TableErrorState message={<ErrorText text={i18n.translation.common.errorLoadingData} />} />
  ) : !rules ? (
    <TableLoadingState />
  ) : rules.length === 0 ? (
    <TableEmptyState message={i18n.translation.common.noItemsFound} />
  ) : undefined;

  return (
    <ModalDialog
      onClose={onClose}
      title={i18n.translation.accessControl.rules.importTitle}
      isSubmitting={isSubmitting}
      buttons={[
        <Button key={0} onClick={onClose} variant="secondary">
          {i18n.translation.common.cancel}
        </Button>,
        <Button key={1} onClick={importRules} disabled={selection.selectedIds.length === 0}>
          {`${i18n.translation.accessControl.rules.import} (${selection.selectedIds.length})`}
        </Button>,
      ]}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1em" }}>
        <FormField label={i18n.translation.accessControl.rules.importFrom}>
          {({ labelId }) => (
            <Select<SourceOption, false, SourceGroup>
              inputId={labelId}
              options={sourceGroups}
              isLoading={!modules}
              placeholder={`${i18n.translation.common.select}...`}
              getOptionLabel={(x) => x.label}
              getOptionValue={(x) => x.key}
              value={from ?? null}
              // Not aborting the previous app's or module's request: it has its own cache key, and an
              // aborted one would be cached as an error, shown when switching back to it.
              onChange={(option: SourceOption | null) => {
                selection.reset();
                setFrom(option ?? undefined);
              }}
              isDisabled={isSubmitting}
            />
          )}
        </FormField>

        {!!from && (
          <>
            <MessageBox type="informative" content={i18n.translation.accessControl.rules.importHint} />

            <Table emptyState={tableStatus} wrapperStyle={{ maxHeight: "50vh", overflowY: "auto" }}>
              <thead>
                <TableRow>
                  <TableHeadCell style={{ width: "1%" }}>
                    <input
                      ref={selection.headerCheckboxRef}
                      type="checkbox"
                      checked={selection.allSelected}
                      disabled={importableEmails.length === 0 || isSubmitting}
                      aria-label={i18n.translation.accessControl.rules.selectAll}
                      onChange={(event) => selection.toggleAll(event.target.checked)}
                    />
                  </TableHeadCell>
                  <TableHeadCell align="left">{i18n.translation.accessControl.rules.email}</TableHeadCell>
                  <TableHeadCell align="left">{i18n.translation.accessControl.rules.status}</TableHeadCell>
                  <TableHeadCell align="left">{i18n.translation.accessControl.rules.reason}</TableHeadCell>
                  <TableHeadCell align="left">{i18n.translation.accessControl.rules.currentStatus}</TableHeadCell>
                </TableRow>
              </thead>
              {!error && !!rules && (
                <tbody>
                  {rules.map((rule) => (
                    <TableRow key={rule.email}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selection.isSelected(rule.email)}
                          disabled={!isImportable(rule) || isSubmitting}
                          aria-label={rule.email}
                          onChange={(event) => selection.toggle(rule.email, event.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        {rule.email}
                        {rule.isCurrentUser && (
                          <div>
                            <small>{i18n.translation.accessControl.errors.ruleCannotTargetSelf}</small>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{renderStatus(rule.status)}</TableCell>
                      <TableCell>{rule.reason}</TableCell>
                      <TableCell>
                        {!!rule.currentStatus && renderStatus(rule.currentStatus)}
                        {rule.currentStatus === rule.status && ` ${i18n.translation.common.alreadyPresent}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              )}
            </Table>
          </>
        )}
      </div>
    </ModalDialog>
  );
};

export default ImportAccessRulesModal;
